import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Input, Select, Space, Tag, Image, message, Modal, Form, Radio, Divider, Typography } from 'antd';
import { PlusOutlined, SearchOutlined, RobotOutlined, ThunderboltOutlined, BulbOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { lifetreeApi } from '../api/lifetreeApi';
import { LifetreeItem, LifetreeListRequest, CreateLifetreeRequest, LifetreeSuggestionResponse } from '../api/models/Lifetree';
import LifetreeSuggestionsModal from './LifetreeSuggestionsModal';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

const Lifetrees: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [lifetrees, setLifetrees] = useState<LifetreeItem[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState<LifetreeListRequest>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [createLoading, setCreateLoading] = useState(false);

  // AI Generation states
  const [creationMode, setCreationMode] = useState<'manual' | 'ai'>('manual');
  const [aiStep, setAiStep] = useState<'input' | 'review'>('input');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [generatedMetadata, setGeneratedMetadata] = useState<{ title: string; description: string; outline: string } | null>(null);

  // Suggestions state
  const [suggestionsModalVisible, setSuggestionsModalVisible] = useState(false);
  const [suggestions, setSuggestions] = useState<LifetreeSuggestionResponse['suggestions']>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const fetchLifetrees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await lifetreeApi.listLifeTrees(filters);
      setLifetrees(response.data);
      setPagination({
        current: response.pagination.pageNumber,
        pageSize: response.pagination.pageSize,
        total: response.pagination.total,
      });
    } catch (error: any) {
      message.error('Failed to fetch lifetrees: ' + (error.response?.data?.messages?.[0] || error.message));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLifetrees();
  }, [fetchLifetrees]);

  const handleTableChange = (pagination: any) => {
    setFilters(prev => ({
      ...prev,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({
      ...prev,
      search: value,
      pageNumber: 1, // Reset to first page when searching
    }));
  };

  const handleStatusFilter = (value: string) => {
    setFilters(prev => ({
      ...prev,
      status: value as 'draft' | 'active' | 'inactive' | undefined,
      pageNumber: 1, // Reset to first page when filtering
    }));
  };

  const handleCreateLifetree = () => {
    setCreateModalVisible(true);
  };

  const handleSuggestIdeas = async () => {
    setSuggestionsModalVisible(true);
    setSuggestionsLoading(true);
    try {
      const lang = createForm.getFieldValue('language') || 'vi';
      const result = await lifetreeApi.suggestLifetreeIdeas(lang);
      setSuggestions(result.suggestions);
    } catch (error: any) {
      message.error('Gợi ý ý tưởng thất bại: ' + (error.response?.data?.messages?.[0] || error.message));
      setSuggestionsModalVisible(false);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleSelectSuggestion = (suggestion: any) => {
    setSuggestionsModalVisible(false);
    setCreateModalVisible(true);
    setCreationMode('ai');
    setAiStep('input');
    setAiPrompt(suggestion.description);
    
    // Also set some initial values in the form if we skip to review later, 
    // but here we keep user in 'input' step so they can refine the idea.
    createForm.setFieldsValue({
      title: suggestion.title,
      tags: suggestion.tags.join(', ')
    });
  };

  const handleCreateModalCancel = () => {
    setCreateModalVisible(false);
    setCreationMode('manual');
    setAiStep('input');
    setAiPrompt('');
    setGeneratedMetadata(null);
    createForm.resetFields();
  };

  const handleGenerateAiMetadata = async () => {
    if (!aiPrompt.trim()) {
      message.warning('Please enter your story idea!');
      return;
    }

    setAiLoading(true);
    try {
      const language = createForm.getFieldValue('language') || 'en';
      const result = await lifetreeApi.generateLifetreeMetadata(aiPrompt, language);
      setGeneratedMetadata(result);
      setAiStep('review');
      
      // Update form fields with generated values
      createForm.setFieldsValue({
        title: result.title,
        description: result.description,
        outline: result.outline
      });
    } catch (error: any) {
      message.error('AI Generation failed: ' + (error.response?.data?.messages?.[0] || error.message));
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreateSubmit = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);
      
      const createData: CreateLifetreeRequest = {
        title: values.title,
        description: values.description,
        imageUrl: values.imageUrl,
        tags: values.tags ? (typeof values.tags === 'string' ? values.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag !== '') : values.tags) : [],
        language: values.language,
        outline: values.outline,
      };

      await lifetreeApi.createLifetree(createData);
      message.success('Lifetree created successfully!');
      
      // Close modal and reset form
      handleCreateModalCancel();
      
      // Refresh the list
      fetchLifetrees();
    } catch (error: any) {
      if (error.errorFields) return; // Validation error
      message.error('Failed to create lifetree: ' + (error.response?.data?.messages?.[0] || error.message));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleLifetreeClick = (record: LifetreeItem) => {
    navigate(`/lifetrees/${record.id}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'draft':
        return 'orange';
      case 'inactive':
        return 'red';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: LifetreeItem) => (
        <Button 
          type="link" 
          onClick={() => handleLifetreeClick(record)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      render: (imageUrl: string | null) => (
        imageUrl ? (
          <Image
            width={60}
            src={imageUrl}
            alt="Lifetree"
            style={{ borderRadius: '4px' }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
          />
        ) : (
          <div style={{ 
            width: 50, 
            height: 50, 
            backgroundColor: '#f0f0f0', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            borderRadius: '4px',
            color: '#999'
          }}>
            No Image
          </div>
        )
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      render: (lang: string) => lang ? lang.toUpperCase() : '-',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>LifeTrees</h2>
        <Space>
          <Button 
            icon={<BulbOutlined />} 
            onClick={handleSuggestIdeas}
          >
            Gợi ý ý tưởng mới (AI)
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleCreateLifetree}
          >
            Create LifeTree
          </Button>
        </Space>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Search
            placeholder="Search lifetrees..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            onSearch={handleSearch}
            style={{ width: 300 }}
          />
          <Select
            placeholder="Filter by status"
            allowClear
            style={{ width: 150 }}
            onChange={handleStatusFilter}
          >
            <Option value="draft">Draft</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={lifetrees}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
        loading={loading}
      />

      <Modal
        title={creationMode === 'ai' ? (aiStep === 'input' ? "Tạo Lifetree bằng AI" : "Review AI Outline") : "Create New Lifetree"}
        open={createModalVisible}
        onOk={creationMode === 'ai' && aiStep === 'input' ? handleGenerateAiMetadata : handleCreateSubmit}
        onCancel={handleCreateModalCancel}
        confirmLoading={createLoading || aiLoading}
        width={700}
        okText={creationMode === 'ai' && aiStep === 'input' ? "Sáng tạo bằng AI" : (creationMode === 'ai' && aiStep === 'review' ? "Tạo Lifetree" : "Create")}
      >
        <div style={{ marginBottom: 16 }}>
          <Radio.Group 
            value={creationMode} 
            onChange={(e) => {
              setCreationMode(e.target.value);
              setAiStep('input');
            }}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="manual">Thủ công</Radio.Button>
            <Radio.Button value="ai"><RobotOutlined /> AI Assistant</Radio.Button>
          </Radio.Group>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <Form
          form={createForm}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            label="Language"
            name="language"
            initialValue="en"
            rules={[{ required: true, message: 'Please select the language!' }]}
          >
            <Select placeholder="Select language">
              <Option value="en">English (en)</Option>
              <Option value="vi">Vietnamese (vi)</Option>
            </Select>
          </Form.Item>

          {creationMode === 'ai' && aiStep === 'input' ? (
            <div style={{ padding: '12px 0' }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Nhập ý tưởng sơ khai về câu chuyện của bạn (ví dụ: Một chàng trai nghèo vươn lên thành tỷ phú công nghệ, hoặc câu chuyện tình yêu vượt thời gian...)
              </Text>
              <TextArea
                rows={6}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Nhập ý tưởng tại đây..."
                autoFocus
              />
            </div>
          ) : (
            <>
              {creationMode === 'ai' && aiStep === 'review' && (
                <div style={{ marginBottom: 20, padding: '12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
                  <Space><ThunderboltOutlined style={{ color: '#52c41a' }} /> <Text strong>AI đã phác thảo xong nội dung!</Text></Space>
                  <Text style={{ display: 'block', marginTop: 4 }}>Bạn có thể review và chỉnh sửa lại các thông tin dưới đây trước khi lưu chính thức.</Text>
                </div>
              )}
              
              <Form.Item
                label="Title"
                name="title"
                rules={[
                  { required: true, message: 'Please input the title!' },
                  { max: 255, message: 'Title must be less than 255 characters!' }
                ]}
              >
                <Input placeholder="Enter lifetree title" />
              </Form.Item>

              <Form.Item
                label="Description"
                name="description"
                rules={[
                  { max: 1000, message: 'Description must be less than 1000 characters!' }
                ]}
              >
                <TextArea 
                  rows={3} 
                  placeholder="Enter lifetree description (optional)"
                />
              </Form.Item>

              <Form.Item
                label="Outline (Mạch truyện tổng thể)"
                name="outline"
                rules={[{ required: creationMode === 'ai', message: 'Outline is required in AI mode' }]}
              >
                <TextArea 
                  rows={6} 
                  placeholder="Phác thảo mạch truyện tổng thể (Sinh bởi AI hoặc tự viết)"
                />
              </Form.Item>

              <Form.Item
                label="Image URL"
                name="imageUrl"
                rules={[
                  { max: 255, message: 'Image URL must be less than 255 characters!' }
                ]}
              >
                <Input placeholder="Enter image URL (optional)" />
              </Form.Item>

              <Form.Item
                label="Tags"
                name="tags"
                help="Enter tags separated by commas (e.g., Tech, Business, Adventure)"
              >
                <Input placeholder="Tech, Business, Adventure" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <LifetreeSuggestionsModal
        visible={suggestionsModalVisible}
        onCancel={() => setSuggestionsModalVisible(false)}
        suggestions={suggestions}
        loading={suggestionsLoading}
        onSelectSuggestion={handleSelectSuggestion}
      />
    </div>
  );
};

export default Lifetrees; 